import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { BaseModalProps } from '@/features/modal/types';
import { logException } from '@/utils/logger';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import BffApiService from '@/shared/bffApiService';
import LoadingSpinner from '@/components/shared/spinner/LoadingSpinner';
import IdScanFinished from '@/features/modal/modals/id-scan/IdScanFinished';
import IdScanError from '@/features/modal/modals/id-scan/IdScanError';
import { selectUser } from '@/store/selectors/userSelectors';
import { selectWalletError } from '@/store/wallet/selectors';
import { selectShowFluidQuickDeposit } from '@/store/ui/selectors';
import { getUserWallet } from '@/store/wallet/actions';
import { useCashierShow } from '@/features/cashier/useCashier';
import { kycGetDocuments } from '@/store/actions/kycActions';
import { getUser } from '@/store/actions/user';
import { openZendesk } from '@/components/Zendesk';
import { CHECKIN_API_URL, BUILD_MODE } from '@/constants/constants';
import { getGetIdFlow } from './getGetIdFlow';
import { BffGetIdScanStatusResponse, BffIdScanStatusEvent } from '@lucky7ventures/bff-types';
import IdScanProcessing from '@/features/modal/modals/id-scan/IdScanProcessing';
import CloseButton from '@/components/UI/Buttons/CloseButton';
import { selectLocale } from '@/store/selectors/commonSelectors';
import { mapLocaleToLang } from '@/utils/utils';

const GETID_SDK_SRC = 'https://cdn.getid.cloud/sdk/getid-web-sdk-launcher-v7.min.js';

type ModalState = 'loading' | 'sdk_active' | 'polling' | 'result' | 'error';

function loadGetIdScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.getidWebSdk) {
      resolve();
      return;
    }

    let script = document.querySelector(
      `script[src="${GETID_SDK_SRC}"]`,
    ) as HTMLScriptElement | null;

    if (script) {
      script.addEventListener('load', () => resolve());
      script.addEventListener('error', () => reject(new Error('Failed to load GetID SDK')));
      return;
    }

    script = document.createElement('script');
    script.src = GETID_SDK_SRC;
    script.async = true;

    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load GetID SDK'));

    document.body.appendChild(script);
  });
}

export const CheckinIdScanModal = ({ closeModal }: BaseModalProps) => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const showQuickDeposit = useSelector(selectShowFluidQuickDeposit);
  const walletError = useSelector(selectWalletError);
  const showCashier = useCashierShow();
  const started = useRef(false);
  const sdkInstanceRef = useRef<{ unmount: () => void } | null>(null);
  const locale = useSelector(selectLocale);
  const flow = getGetIdFlow(BUILD_MODE, locale);

  const [modalState, setModalState] = useState<ModalState>('loading');
  const [scanResult, setScanResult] = useState<BffGetIdScanStatusResponse | null>(null);

  const { request: startCheckinIdScan } = useBffApiRequest<{
    token: string;
    idScanSessionId: string;
  }>();

  const { request: getIdScanStatusRequest, stopPolling: stopGetIdScanStatusPolling } =
    useBffApiRequest<BffGetIdScanStatusResponse>();

  const handleError = useCallback(() => {
    setModalState('error');
    stopGetIdScanStatusPolling();
  }, [stopGetIdScanStatusPolling]);

  const handleOnClose = () => {
    triggerGTag(GTagEvents.gbg_id_scan_modal_closed);
    sdkInstanceRef.current?.unmount();
    sdkInstanceRef.current = null;
    closeModal();
    stopGetIdScanStatusPolling();
  };

  const startPolling = useCallback(
    (journeyRef: string) => {
      setModalState('polling');
      getIdScanStatusRequest({
        apiMethod: BffApiService.getIdScanStatus,
        pathParams: { journeyRef },
        successCallback: data => {
          if (
            data.event === BffIdScanStatusEvent.Finished ||
            data.event === BffIdScanStatusEvent.Error
          ) {
            stopGetIdScanStatusPolling();
            if (data.event === BffIdScanStatusEvent.Error) {
              setModalState('error');
            } else {
              setScanResult(data);
              setModalState('result');
            }

            dispatch(kycGetDocuments());
            dispatch(getUser());
          }
        },
        pollingInterval: 5000,
        errorCallback: handleError,
      });
    },
    [dispatch, getIdScanStatusRequest, handleError, stopGetIdScanStatusPolling],
  );

  const initSdk = useCallback(
    (token: string, idScanSessionId: string) => {
      if (!window.getidWebSdk || !user?.userId) {
        return;
      }

      const config = {
        apiUrl: CHECKIN_API_URL,
        jwt: token,
        containerId: 'checkin-id-scan',
        flowName: flow,
        locale: mapLocaleToLang(locale),
        styles: {
          '--gi-custom-inner-height-mobile': 'calc(100vh - 100px)',
        },
        metadata: {
          externalId: idScanSessionId,
          labels: {
            userEmail: user?.email,
            userId: user?.userId,
          },
        },
        profile: [
          { value: user.firstName, category: 'First name' },
          { value: user.lastName, category: 'Last name' },
          { value: user.birthDate.slice(0, 10), category: 'Date of birth' },
        ],
        onComplete: () => {
          startPolling(idScanSessionId);
        },
        onFail: () => {
          handleError();
        },
        onClose: () => {
          handleOnClose();
        },
        onReady: () => {
          setModalState('sdk_active');
        },
      };

      sdkInstanceRef.current?.unmount();
      window.getidWebSdk
        .init(config)
        .then(sdk => {
          sdkInstanceRef.current = sdk;
        })
        .catch(() => {
          handleError();
        });
    },
    [handleError, startPolling, user, flow],
  );

  const initialize = useCallback(async () => {
    setModalState('loading');
    setScanResult(null);

    try {
      await loadGetIdScript();

      startCheckinIdScan({
        apiMethod: BffApiService.startCheckinIdScan,
        successCallback: data => {
          initSdk(data.token, data.idScanSessionId);
        },
        errorCallback: handleError,
      });
    } catch (err) {
      logException(err);
      handleError();
    }
  }, [handleError, initSdk, startCheckinIdScan]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally run only on mount
  useEffect(() => {
    if (!user?.userId || started.current) {
      return;
    }
    started.current = true;
    initialize();
  }, [user]);

  // Cleanup on unmount — separate effect so it's never overwritten by the init effect
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    return () => {
      sdkInstanceRef.current?.unmount();
      sdkInstanceRef.current = null;
      stopGetIdScanStatusPolling();
    };
  }, []);

  const handleRetry = () => {
    initialize();
  };

  const openCashierAction = () => {
    triggerGTag(GTagEvents.header_open_cashier);
    if (walletError) {
      dispatch(getUserWallet());
      return;
    }
    showQuickDeposit ? showCashier({ transactionType: 'quick-deposit' }) : showCashier();
    closeModal();
  };

  const contactSupport = () => {
    openZendesk();
    stopGetIdScanStatusPolling();
    closeModal();
  };

  const isLoading = modalState === 'loading';
  const isPolling = modalState === 'polling';
  const showSdk = modalState === 'sdk_active';
  const showResult = modalState === 'result' && scanResult;
  const showError = modalState === 'error';

  return (
    <div
      className="relative w-full min-w-[320px] sm:min-w-[500px] max-w-[720px] min-h-[350px] rounded-lg bg-slate-50 "
      data-cy="checkin-id-scan"
    >
      <div id="checkin-id-scan" className={showSdk ? 'min-h-[400px]' : 'hidden'} />
      {(isPolling || showResult || showError) && (
        <CloseButton dataCy="close-modal" onClick={handleOnClose} />
      )}

      {(isLoading || isPolling || showResult || showError) && (
        <div className="p-4 md:p-8 flex justify-center">
          {isLoading && (
            <div className="flex w-full h-full items-center justify-center min-h-[300px]">
              <LoadingSpinner mode="dark" />
            </div>
          )}

          {isPolling && <IdScanProcessing />}

          {showResult && (
            <IdScanFinished
              status={scanResult.status}
              rejectedOnClick={contactSupport}
              approvedOnClick={openCashierAction}
              ctaOnClick={handleRetry}
            />
          )}

          {showError && <IdScanError ctaOnClick={handleRetry} />}
        </div>
      )}
    </div>
  );
};
